import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {IDsDropdownOption} from "../../../../../shared/components/ds-dropdown/ds-dropdown-option";
import {filter, first} from "rxjs/operators";
import {WebsiteService} from "../../../../website.service";
import {
  NotificationDialogComponent
} from "../../../../../shared/components/dialogs/notification-dialog/notification-dialog.component";
import {DialogService} from "../../../../../../ng2-bootstrap-modal/dialog.service";
import {Store} from "@ngrx/store";
import * as fromStore from '../../../../../store';
import {FormControl} from "@angular/forms";
import {NotifierComponent} from "../../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../../shared/components/notifier/notification-type";
import {TypeUtils} from "../../../../../shared/utils/type-utils";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";
import {UserSubscription, UserSubscriptionCategory} from "../../../../../user/user-subscription";
import {DbModalService} from "../../../../../modules/modal/modal";
import {User} from "../../../../../user/user";
import {
  DemoProgressDoneComponent
} from "../../../../../shared/components/dialogs/account-upgraded-dialog/demo-progress-done/demo-progress-done.component";
import {OneMonthFreeComponent} from "../../../../../shared/components/dialogs/one-month-free/one-month-free.component";

export interface IAIBuilderPage {
  id: number;
  title: string;
  recreated: boolean;
  link?: string;
  condition?: string;
  lastModified: Date;
  modifiedDate: Date;
  postedDate: Date;
  pageStatus: 'draft' | 'publish';
  createdWith: string;
  conditionCount: number;
  pageType: string;
  frontPage: boolean;
  templateType: string;
}

const STATUS_OPTIONS = [
  {
    value: 'draft',
    label: 'Draft'
  },
  {
    value: 'publish',
    label: 'Published'
  }
];

@Component({
    selector: 'db-ai-builder-page-item',
    templateUrl: './ai-builder-page-item.component.html',
    styleUrls: ['./ai-builder-page-item.component.scss'],
    standalone: false
})
export class AiBuilderPageItemComponent implements OnInit {

  @Input() demoUser?: boolean;
  @Input() heads: string[];
  @Input() page: IAIBuilderPage;
  @Input() websiteId: number;
  @Input() websiteAdminUrl: string;
  @Input() websiteHomeUrl: string;
  @Input() pagesTab?: boolean;
  @Input() slidesTab?: boolean;
  @Input() showCreatedWithAiBtn?: boolean;
  @Input() currentUserSubscription: UserSubscription;
  @Output() onClickAction?: EventEmitter<any> = new EventEmitter<any>();

  public statusOptions: IDsDropdownOption[] = STATUS_OPTIONS;
  public status: FormControl;
  public UserSubscriptionCategory: any = UserSubscriptionCategory;
  public paidClient: boolean;
  public user: User;
  public isMobile: boolean;

  constructor(
    private _store: Store<fromStore.DbStoreState>,
    private _websiteService: WebsiteService,
    private _dialogService: DialogService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _modalService: DbModalService,
  ) {
  }

  ngOnInit() {
    this.isMobile = window.innerWidth <= 991;
    this.status = new FormControl(this.page.pageStatus);
    this.paidClient = [ this.UserSubscriptionCategory.PLATFORM, this.UserSubscriptionCategory.DedicatedVM, this.UserSubscriptionCategory.DedicatedVM, this.UserSubscriptionCategory.BOOSTER].includes(this.currentUserSubscription.type)
    || (this.currentUserSubscription?.trialClient && !this.demoUser);
    this.getUser();
  }

  public getUser(): void {
    this._store.select(fromStore.getUser)
      .pipe(
        filter(user => !!user)
      )
      .subscribe(user => this.user = user);
  }

  public statusChange(status: IDsDropdownOption['value'], page): void {
    this.showNotificationDialog(page.id, status, page.pageStatus);
  }

  public showNotificationDialog(pageId, action, pageStatus): void {
    let headerText;
    let messageText;
    let btnText;
    let btnColor = "blue";
    switch (action) {
      case 'setFrontPage':
        headerText = 'Set as frontpage';
        messageText = 'Are you sure you want to set as frontpage?';
        btnText = 'Set';
        break;
      case 'remove':
        headerText = 'Page remove';
        messageText = 'Are you sure you want to delete this page?';
        btnText = 'Remove';
        btnColor = 'red';
        break;
      case 'draft':
        headerText = 'Switch to draft';
        messageText = 'Are you sure you want to unpublish this page?';
        btnText = 'Confirm';
        break;
      case 'publish':
        headerText = 'Publish the page';
        messageText = 'Are you sure you want to publish this page?';
        btnText = 'Publish';
        break;
    }

    this._dialogService.addDialog(NotificationDialogComponent, {
      header: headerText,
      message: messageText,
      buttonText: btnText,
      buttonColor: btnColor || 'blue',
      action: action,
      hasCloseButton: true,
      showCancelButton: true
    })
      .subscribe((confirmed: string): void => {
        if (confirmed) {
          this.onClickAction.emit(true);
          if (confirmed === 'remove') {
            this._deletePage(pageId);
          } else if (confirmed === 'setFrontPage') {
            this._setFrontPage(pageId);
          } else {
            this._publishPage(pageId, action);
          }
        } else {
          this.status.setValue(pageStatus);
        }
      });
  }

  private _deletePage(pageId): void {
    this._websiteService.removePage(this.websiteId, pageId)
      .pipe(
        first(response => !!response)
      )
      .subscribe(response => {
        if (response.success) {
          this._store.dispatch(new fromStore.LoadBuilderCreatedPages(this.websiteId));
          response.data && TypeUtils.isDefined(response.data.builder_status) && response.data.message
          && NotifierComponent.notify(new Notification(response.data.message, !!response.data.builder_status ? NotificationType.SUCCESS : NotificationType.DANGER));
        } else {
          NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
        }
        this.onClickAction.emit(false);
      });
  }

  private _publishPage(pageId, action): void {
    this._websiteService.publishPage(this.websiteId, pageId, action)
      .pipe(
        first(response => !!response)
      )
      .subscribe(response => {
        if (response.success) {
          this._store.dispatch(new fromStore.LoadBuilderCreatedPages(this.websiteId));
          response.data && TypeUtils.isDefined(response.data.builder_status) && response.data.message
          && NotifierComponent.notify(new Notification(response.data.message, !!response.data.builder_status ? NotificationType.SUCCESS : NotificationType.DANGER));
        } else {
          NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
        }
        this.onClickAction.emit(false);
      });
  }

  private _setFrontPage(pageId): void {
    this._websiteService.setFrontPage(this.websiteId, pageId)
      .pipe(
        first(response => !!response)
      )
      .subscribe(response => {
        if (response.success) {
          this._store.dispatch(new fromStore.LoadBuilderCreatedPages(this.websiteId));
          response.data && TypeUtils.isDefined(response.data.builder_status) && response.data.message
          && NotifierComponent.notify(new Notification(response.data.message, !!response.data.builder_status ? NotificationType.SUCCESS : NotificationType.DANGER));
        } else {
          NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
        }
        this.onClickAction.emit(false);
      });
  }

  public showCheckoutUpgradeDialog(): void {
    const analyticsData = {
      eventCategory: 'Upgrade Offer',
      eventAction: 'Locked button click',
      eventLabel: 'Edit website',
    };
    const modal = this._modalService.open(OneMonthFreeComponent, {height: 600, width: 940});
    modal.componentInstance.mode = 'editor';
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  handleEditClick(): void {
    if (this.isMobile) {
      event.preventDefault();
      // Open dialog for mobile
      const modal = this._modalService.open(DemoProgressDoneComponent, {hasCloseBtn: true});
      modal.componentInstance.forMobileEdit = true;
      modal.componentInstance.websiteId = this.websiteId;
      modal.componentInstance.user = this.user;
    }
  }

}
